import React, { useRef, useEffect } from 'react';
import styles from './ChatTextArea.module.scss';
import { Text } from '../Typography';

export interface ChatTextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlurChange?: (value: string) => void;
  fullWidth?: boolean;
  error?: string;
  maxRows?: number;
  minRows?: number;
}

const ChatTextArea: React.FC<ChatTextAreaProps> = ({
  label,
  value,
  onChange,
  onBlurChange,
  fullWidth = false,
  error,
  className,
  id,
  maxRows = 6,
  minRows = 1,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate line height from computed styles
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight) || 24;

    // Calculate new height based on content
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);

    // Set the height
    textarea.style.height = `${newHeight + 2}px`;
  };

  // Resize on value change
  useEffect(() => {
    autoResize();
  }, [value, minRows, maxRows]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const textareaClasses = [
    styles.textarea,
    error ? styles.error : '',
    fullWidth ? styles.fullWidth : '',
    className || '',
  ].join(' ');

  const textareaId = id || `chat-textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className={`${styles.textareaContainer} ${fullWidth ? styles.fullWidth : ''}`}>
      {label && (
        <label htmlFor={textareaId} className={styles.labelWrapper}>
          <Text size="small" weight="medium">
            {label}
          </Text>
        </label>
      )}
      <textarea
        ref={textareaRef}
        id={textareaId}
        className={textareaClasses}
        value={value}
        onChange={handleChange}
        onBlur={() => onBlurChange?.(value)}
        rows={minRows}
        {...props}
      />
      {error && (
        <Text size="small" color="#FF6A55" className={styles.errorMessage}>
          {error}
        </Text>
      )}
    </div>
  );
};

export default ChatTextArea;
